import {
  type AgentInboundMessageMetadata,
  type AgentInboundMessagePill,
  type SessionArtifactWrite,
} from '@air/server-sdk';

export type WorkspaceAgentOutboundArtifactHandle = NonNullable<AgentInboundMessageMetadata['artifacts']>[number];

export type WorkspaceAgentOutboundArtifacts = {
  artifacts: SessionArtifactWrite[];
  handles: WorkspaceAgentOutboundArtifactHandle[];
  /** List pills with membership, for the optimistic bubble only. The request metadata stays handles. */
  optimisticPills: AgentInboundMessagePill[];
  pills: AgentInboundMessagePill[];
};

const assetPillFromListItem = (
  item: Extract<AgentInboundMessagePill, { type: 'assetList' }>['items'][number],
): AgentInboundMessagePill => ({
  assetId: item.assetId,
  id: item.versionId ?? item.assetId,
  type: 'asset',
  ...(item.versionId ? { versionId: item.versionId } : {}),
});

const boardPillFromListItem = (
  item: Extract<AgentInboundMessagePill, { type: 'boardList' }>['items'][number],
): AgentInboundMessagePill => ({
  boardId: item.boardId,
  id: item.boardId,
  type: 'board',
});

/**
 * Uncited multi-selects collapse to `assetList` / `boardList` pills. Those leave as session
 * artifacts. A one-row list is a singular entity. Singular pills pass through.
 */
export const splitWorkspaceAgentOutboundListPills = (
  pills: readonly AgentInboundMessagePill[],
): WorkspaceAgentOutboundArtifacts => {
  const artifacts: SessionArtifactWrite[] = [];
  const handles: WorkspaceAgentOutboundArtifactHandle[] = [];
  const optimisticPills: AgentInboundMessagePill[] = [];
  const singularPills: AgentInboundMessagePill[] = [];

  for (const pill of pills) {
    if (pill.type === 'assetList') {
      const [onlyAsset] = pill.items;

      if (pill.items.length === 1 && onlyAsset) {
        singularPills.push(assetPillFromListItem(onlyAsset));
        continue;
      }

      if (pill.items.length < 2) {
        continue;
      }

      artifacts.push({ id: pill.id, rows: pill.items, type: 'assetList' });
      handles.push({ count: pill.items.length, id: pill.id, origin: 'artifact', type: 'assetList' });
      optimisticPills.push(pill);
      continue;
    }

    if (pill.type === 'boardList') {
      const [onlyBoard] = pill.items;

      if (pill.items.length === 1 && onlyBoard) {
        singularPills.push(boardPillFromListItem(onlyBoard));
        continue;
      }

      if (pill.items.length < 2) {
        continue;
      }

      artifacts.push({ id: pill.id, rows: pill.items, type: 'boardList' });
      handles.push({ count: pill.items.length, id: pill.id, origin: 'artifact', type: 'boardList' });
      optimisticPills.push(pill);
      continue;
    }

    singularPills.push(pill);
  }

  return { artifacts, handles, optimisticPills, pills: singularPills };
};
